import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DateSearch from "../components/DateSearch";
import TableHeaderFilter from "../components/TableHeaderfilter";
import { SERVER_URL } from "../config";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import {
    Bars3Icon,
    CheckCircleIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";

/* ── stat card (matches AdminAttendanceOverview StatCard) ──────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ── stat tile (large number tiles) ───────────────────────────────────────── */
const StatTile = ({ label, value, colorClass, borderClass, textClass, numClass }) => (
    <div className={`flex flex-col gap-1 px-5 py-4 rounded-xl border ${borderClass} ${colorClass}`}
        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
        <p className={`text-[10px] font-bold uppercase tracking-widest ${textClass}`}>{label}</p>
        <p className={`text-3xl font-extrabold ${numClass}`}>{value}</p>
    </div>
);

/* ════════════════════════════════════════════════════════════════════════════ */
const EmployeeTaskPage = () => {

    /* ---------------- SIDEBAR ---------------- */
    const [sidebarOpen, setSidebarOpen] = useState(false);

    /* ---------------- LOGGED EMPLOYEE ---------------- */
    const employeeData = JSON.parse(localStorage.getItem("employee"));
    const loggedEmployeeId = employeeData?.employee_id;
    const loggedEmployeeName = employeeData?.employee_name;

    /* ---------------- DATA STATES ---------------- */
    const [tasks, setTasks] = useState([]);
    const [taskType, setTaskType] = useState("daily");
    const [dateRange, setDateRange] = useState({ start: "", end: "" });

    /* ---------------- FILTER STATES ---------------- */
    const [taskFilter, setTaskFilter] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);

    /* ---------------- ACTION POPUP ---------------- */
    const [actionTaskId, setActionTaskId] = useState(null);
    const [resultText, setResultText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    /* ---------------- FETCH TASKS ---------------- */
    const fetchTasks = async () => {
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/TaskHistory.php`, {
                employee_id: loggedEmployeeId,
                start_date: dateRange.start,
                end_date: dateRange.end
            });
            const data = res.data;
            setTasks(data.tasks || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [taskType, dateRange]);

    /* ---------------- DATE SEARCH ---------------- */
    const handleDateSearch = (start, end) => {
        setDateRange({ start, end });
    };

    /* ---------------- FILTER OPTIONS ---------------- */
    const taskOptions = [...new Set(tasks.map(t => t.title))].map(t => ({ label: t, value: t }));
    const typeOptions = [
        { label: "Daily",   value: "daily" },
        { label: "Monthly", value: "monthly" },
        { label: "Yearly",  value: "yearly" }
    ];

    /* ---------------- FILTERED TASKS ---------------- */
    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            if (taskFilter.length && !taskFilter.includes(task.title))     return false;
            if (typeFilter.length && !typeFilter.includes(task.task_type)) return false;
            return true;
        });
    }, [tasks, taskFilter, typeFilter]);

    /* ---------------- FILTERED SUMMARY ---------------- */
    const filteredSummary = useMemo(() => {
        const total     = filteredTasks.length;
        const completed = filteredTasks.filter(t => t.status === "completed").length;
        const pending   = total - completed;
        return { total, completed, pending };
    }, [filteredTasks]);

    /* ---------------- GRAPH DATA ---------------- */
    const graphData = useMemo(() => {
        const activeTypes = typeFilter.length > 0 ? typeFilter : [taskType];
        let baseTasks = tasks.filter(t => activeTypes.includes(t.task_type));

        if (dateRange.start && dateRange.end && taskType === "daily") {
            baseTasks = tasks.filter(t => ["daily", "monthly"].includes(t.task_type));
        }

        const total     = baseTasks.length;
        const completed = baseTasks.filter(t => t.status === "completed").length;
        const pending   = total - completed;

        return [
            { name: loggedEmployeeName || "Me", Assigned: total, Completed: completed, Pending: pending }
        ];
    }, [tasks, taskType, typeFilter, dateRange]);

    /* ---------------- SUBMIT RESULT ---------------- */
    const handleCompleteTask = async (taskId) => {
        if (!resultText.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(`${SERVER_URL}/Rahul/EmployeeTaskStatusUpdate.php`, {
                task_id:     taskId,
                result:      resultText.trim(),
                status:      "completed",
                employee_id: loggedEmployeeId
            });
            setActionTaskId(null);
            setResultText("");
            fetchTasks();
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    /* ════════════════════════════════ JSX ════════════════════════════════ */
    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .att-scrollbar::-webkit-scrollbar-track { background: rgba(226,232,240,0.6); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
            `}</style>

            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    My Tasks
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            {/* Summary stat pills */}
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                                <StatCard label="Total"     value={filteredSummary.total}     colorClass="bg-blue-50 text-blue-700 border border-blue-200"       dotClass="bg-blue-500" />
                                <StatCard label="Completed" value={filteredSummary.completed} colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200" dotClass="bg-emerald-500" />
                                <StatCard label="Pending"   value={filteredSummary.pending}   colorClass="bg-rose-50 text-rose-700 border border-rose-200"         dotClass="bg-rose-500" />
                            </div>
                            {/* Date Search */}
                            <DateSearch onSearch={handleDateSearch} />
                        </div>
                    </div>

                    {/* ── STAT TILES ── */}
                    <div className="grid grid-cols-3 gap-4">
                        <StatTile
                            label="Total Tasks" value={filteredSummary.total}
                            colorClass="bg-blue-50"   borderClass="border-blue-200"
                            textClass="text-blue-600"  numClass="text-blue-800"
                        />
                        <StatTile
                            label="Completed" value={filteredSummary.completed}
                            colorClass="bg-emerald-50" borderClass="border-emerald-200"
                            textClass="text-emerald-600" numClass="text-emerald-800"
                        />
                        <StatTile
                            label="Pending" value={filteredSummary.pending}
                            colorClass="bg-rose-50"   borderClass="border-rose-200"
                            textClass="text-rose-600"  numClass="text-rose-800"
                        />
                    </div>

                    {/* ── BAR CHART ── */}
                    <div className="rounded-xl p-6 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 rounded-full bg-blue-700" />
                            <h3 className="text-sm font-bold text-slate-800">My Assignment vs Completed</h3>
                        </div>

                        {graphData[0]?.Assigned === 0 ? (
                            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No data available</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={graphData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }} barCategoryGap="40%" barGap={4}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.8)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} tickLine={false} axisLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{
                                            background: "#ffffff",
                                            border: "1px solid rgba(226,232,240,1)",
                                            borderRadius: 10, fontSize: 12, color: "#334155",
                                            boxShadow: "0 4px 12px rgba(15,23,42,0.1)"
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11, color: "#64748b", fontWeight: 600 }} />
                                    <Bar dataKey="Assigned"  fill="#3b82f6" radius={[5, 5, 0, 0]} />
                                    <Bar dataKey="Completed" fill="#10b981" radius={[5, 5, 0, 0]} />
                                    <Bar dataKey="Pending"   fill="#f43f5e" radius={[5, 5, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[420px] att-scrollbar">
                            <table className="min-w-full text-sm table-fixed">

                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        {[
                                            {
                                                width: "w-[20%]",
                                                content: (
                                                    <TableHeaderFilter
                                                        label="Task"
                                                        options={taskOptions}
                                                        selectedValues={taskFilter}
                                                        onChange={setTaskFilter}
                                                    />
                                                )
                                            },
                                            {
                                                width: "w-[12%]",
                                                content: (
                                                    <TableHeaderFilter
                                                        label="Type"
                                                        options={typeOptions}
                                                        selectedValues={typeFilter}
                                                        onChange={setTypeFilter}
                                                    />
                                                )
                                            },
                                            { width: "w-[10%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Priority</span> },
                                            { width: "w-[11%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Start Date</span> },
                                            { width: "w-[11%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">End Date</span> },
                                            { width: "w-[14%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</span> },
                                            { width: "w-[10%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span> },
                                            { width: "w-[12%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Result</span> },
                                            { width: "w-[10%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</span> },
                                        ].map((col, i) => (
                                            <th key={i} className={`p-3 text-left ${col.width} border-b border-slate-200`}>
                                                {col.content}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredTasks.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center p-8 text-slate-400 text-sm">
                                                No tasks found.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTasks.map(task => {
                                            const isCompleted  = task.status === "completed";
                                            const isActionOpen = actionTaskId === task.task_id;

                                            return (
                                                <React.Fragment key={task.task_id}>

                                                    <tr className="border-t border-slate-100 transition-colors hover:bg-blue-50/60">

                                                        <td className="p-3 text-slate-800 truncate text-xs font-medium">{task.title}</td>
                                                        <td className="p-3 text-slate-500 capitalize text-xs">{task.task_type}</td>

                                                        {/* Priority badge */}
                                                        <td className="p-3">
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize
                                                                ${task.priority === "high"   ? "bg-rose-50 text-rose-700 border border-rose-200"     :
                                                                  task.priority === "medium" ? "bg-amber-50 text-amber-700 border border-amber-200"   :
                                                                                               "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                                                                {task.priority}
                                                            </span>
                                                        </td>

                                                        <td className="p-3 text-slate-500 text-xs">{task.start_date}</td>
                                                        <td className="p-3 text-slate-500 text-xs">{task.end_date}</td>
                                                        <td className="p-3 text-slate-400 text-xs truncate">{task.description || "—"}</td>

                                                        <td className="p-3">
                                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold
                                                                ${isCompleted
                                                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                                    : "bg-rose-50 text-rose-700 border border-rose-200"
                                                                }`}>
                                                                {isCompleted ? "Completed" : "Pending"}
                                                            </span>
                                                        </td>

                                                        <td className="p-3 text-slate-500 text-xs truncate">{task.result || "—"}</td>

                                                        <td className="p-3">
                                                            {isCompleted ? (
                                                                <div className="flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                                                                    <CheckCircleIcon className="h-4 w-4" />
                                                                    Done
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => {
                                                                        if (isActionOpen) {
                                                                            setActionTaskId(null);
                                                                            setResultText("");
                                                                        } else {
                                                                            setActionTaskId(task.task_id);
                                                                            setResultText("");
                                                                        }
                                                                    }}
                                                                    className="px-3 py-1 text-xs rounded-lg font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 bg-blue-50 transition-colors"
                                                                >
                                                                    Action
                                                                </button>
                                                            )}
                                                        </td>

                                                    </tr>

                                                    {/* INLINE ACTION ROW */}
                                                    {isActionOpen && (
                                                        <tr style={{ background: "#f0f9ff" }}>
                                                            <td colSpan={9} className="px-5 py-4 border-t border-blue-100">
                                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">

                                                                    <div className="flex-1 w-full">
                                                                        <label className="block text-xs text-slate-600 font-semibold mb-1 uppercase tracking-wider">
                                                                            Task Result / Remarks
                                                                        </label>
                                                                        <textarea
                                                                            value={resultText}
                                                                            onChange={(e) => setResultText(e.target.value)}
                                                                            placeholder="Describe what you completed..."
                                                                            rows={2}
                                                                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                                                                        />
                                                                    </div>

                                                                    <div className="flex gap-2 shrink-0">
                                                                        <button
                                                                            onClick={() => handleCompleteTask(task.task_id)}
                                                                            disabled={submitting || !resultText.trim()}
                                                                            className="flex items-center gap-1 px-4 py-2 text-sm font-bold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                                            style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
                                                                        >
                                                                            <CheckCircleIcon className="h-4 w-4" />
                                                                            {submitting ? "Saving..." : "Completed"}
                                                                        </button>

                                                                        <button
                                                                            onClick={() => {
                                                                                setActionTaskId(null);
                                                                                setResultText("");
                                                                            }}
                                                                            className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 rounded-xl transition-colors"
                                                                        >
                                                                            <XMarkIcon className="h-4 w-4" />
                                                                            Cancel
                                                                        </button>
                                                                    </div>

                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}

                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>

                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default EmployeeTaskPage;